import React, { useCallback, useMemo, useState } from "react";

import { faFileArrowDown, faUpload } from "@fortawesome/free-solid-svg-icons";
import { faEye, faEyeSlash } from "@fortawesome/pro-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";

import { bulkSetSideLoadedBusinessesIgnored } from "actions/sideLoadedData";

import AgGridTable from "components/AgGrid/AgGridContainer";
import { RowSelectionMode } from "components/AgGrid/constants";
import { MultiButtonOption } from "components/Button/MultiButton/types";
import { Column, Row } from "components/Layout";
import { SmallText } from "components/Text";

import { AgGridTables } from "constants/aggrid";
import {
  processSideLoadedBusinessCellForExport,
  SideLoadedBusinessAutoSizedColumnIds,
  SideLoadedBusinessColumnDefs,
} from "constants/columnDefinitions/sideLoadedBusiness";
import {
  DeploymentPermission,
  SaleyardPermission,
} from "constants/permissions";

import { useHasExportBusinessesPermission } from "hooks";
import { useHasSaleyardOrDeploymentPermission } from "hooks/useHasPermission";

import {
  openCreateSideLoadedBusinessModal,
  openSideLoadedDataSetsModal,
} from "lib/navigation";

import { pluralize } from "lib/pluralize";

import { downloadSideLoadedDataSetTemplate } from "lib/sideLoadedDataSetTemplate";

import {
  getIsStaff,
  getSideLoadedBusinesses,
  selectSideLoadedBusinessesGridList,
} from "selectors";
import {
  SIDE_LOADED_BUSINESS_TOOLTIP_SHOW_DELAY,
  SideLoadedBusinessRow,
} from "constants/sideLoadedBusinesses";

import { MappingStep } from "components/Business/SideLoadedBusinesses/DataSetsModal/MappingStep";
import { ZoomyDialog } from "components/MaterialDialog";

import { RenameDataSetModal } from "./RenameDataSetModal";
import {
  getSideLoadedBusinessRowId,
  useSideLoadedBusinessGridContext,
} from "./useSideLoadedBusinessGridContext";

const uploadAction = {
  default: true,
  title: "Upload CSV",
  onClick: () => openSideLoadedDataSetsModal(),
  icon: faUpload,
  dataTour: "uploadSideLoadedDataSet",
};

const downloadTemplateAction = {
  title: "Download Template",
  onClick: () => downloadSideLoadedDataSetTemplate(),
  icon: faFileArrowDown,
  dataTour: "downloadSideLoadedDataSetTemplate",
};

const csvExportOptions = {
  processCellCallback: processSideLoadedBusinessCellForExport,
};

export function SideLoadedBusinessManageTable(): React.JSX.Element {
  const dispatch = useDispatch();
  const rowData = useSelector(selectSideLoadedBusinessesGridList);
  const hasExportCsvPermissions = useHasExportBusinessesPermission();
  const canAddDataSet = useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.canAddSideLoadedDataSet,
    SaleyardPermission.canAddSideLoadedDataSet,
  );
  // Staff-only on top of the permission while the feature beds in, so lifting
  // the restriction is just dropping the isStaff term.
  const isStaff = useSelector(getIsStaff);
  const sideLoadedBusinessesById: StateModelById<SideLoadedBusiness> =
    useSelector(getSideLoadedBusinesses);

  const [selectedRecordIds, setSelectedRecordIds] = useState<string[]>([]);
  const selectedRecordCount = selectedRecordIds.length;

  const [editingDataSet, setEditingDataSet] =
    useState<SideLoadedDataSet | null>(null);
  const [renamingDataSet, setRenamingDataSet] =
    useState<SideLoadedDataSet | null>(null);

  const onRowSelectionChange = useCallback(
    // A selected group row arrives here as undefined - it has no record of its own.
    (rows: (SideLoadedBusinessRow | undefined)[]) => {
      const newSelectedRecordIds = rows.filter(Boolean).map(row => row.id);
      if (
        newSelectedRecordIds.length !== selectedRecordCount ||
        !newSelectedRecordIds.every(id => selectedRecordIds.includes(id))
      ) {
        setSelectedRecordIds(newSelectedRecordIds);
      }
    },
    [selectedRecordCount, selectedRecordIds],
  );

  // Split the records from the store, not the selected rows - those are a
  // snapshot from selection time, and don't change when the records do.
  const [ignorableIds, restorableIds] = useMemo(() => {
    const ignorable: string[] = [];
    const restorable: string[] = [];
    selectedRecordIds.forEach(id => {
      const record = sideLoadedBusinessesById[id];
      if (record) {
        (record.isIgnored ? restorable : ignorable).push(id);
      }
    });
    return [ignorable, restorable];
  }, [selectedRecordIds, sideLoadedBusinessesById]);

  const handleBulkSetIgnored = useCallback(
    (isIgnored: boolean) => {
      const ids = isIgnored ? ignorableIds : restorableIds;
      if (ids.length) {
        dispatch(bulkSetSideLoadedBusinessesIgnored(ids, isIgnored));
      }
    },
    [dispatch, ignorableIds, restorableIds],
  );

  const defaultCsvExportParams = useMemo(
    () => ({
      onlySelected: !!selectedRecordCount,
      processCellCallback: processSideLoadedBusinessCellForExport,
    }),
    [selectedRecordCount],
  );

  const handleSelectAndCompare = useCallback((recordId: string) => {
    openCreateSideLoadedBusinessModal(undefined, recordId);
  }, []);

  const context = useSideLoadedBusinessGridContext(
    handleSelectAndCompare,
    "Create Business",
    setEditingDataSet,
    setRenamingDataSet,
  );

  const ignorableCount = ignorableIds.length;
  const restorableCount = restorableIds.length;

  const additionalActions = useMemo<MultiButtonOption[]>(() => {
    const actions: MultiButtonOption[] =
      isStaff && canAddDataSet
        ? [uploadAction, downloadTemplateAction]
        : [downloadTemplateAction];

    if (!context.canIgnore) {
      return actions;
    }

    // Counted on what the action would actually change, so the number promises
    // how many rows move rather than how many are selected.
    return actions.concat([
      {
        title: `Bulk Ignore (${ignorableCount}) ${pluralize(
          "Record",
          ignorableCount,
        )}`,
        icon: faEyeSlash,
        isDisabled: ignorableCount === 0,
        onClick: () => handleBulkSetIgnored(true),
        dataTour: "bulkIgnoreSideLoadedBusinesses",
      },
      {
        title: `Bulk Restore (${restorableCount}) ${pluralize(
          "Record",
          restorableCount,
        )}`,
        icon: faEye,
        isDisabled: restorableCount === 0,
        onClick: () => handleBulkSetIgnored(false),
        dataTour: "bulkRestoreSideLoadedBusinesses",
      },
    ]);
  }, [
    canAddDataSet,
    context.canIgnore,
    handleBulkSetIgnored,
    ignorableCount,
    isStaff,
    restorableCount,
  ]);

  return (
    <Column fullHeight>
      {rowData.length === 0 && (
        <Row paddingVertical={2} paddingHorizontal={2}>
          <SmallText>
            No Legacy Business data has been uploaded yet. Once a data set has
            been uploaded, its businesses will be listed here.
          </SmallText>
        </Row>
      )}
      <AgGridTable
        additionalActions={additionalActions}
        columnDefs={SideLoadedBusinessColumnDefs}
        context={context}
        autoSizeColumnIds={SideLoadedBusinessAutoSizedColumnIds}
        getRowId={getSideLoadedBusinessRowId}
        tooltipShowDelay={SIDE_LOADED_BUSINESS_TOOLTIP_SHOW_DELAY}
        rowData={rowData}
        tableName={AgGridTables.SIDELOADED_BUSINESSES_MANAGE}
        hasExportCsvPermissions={hasExportCsvPermissions}
        exportSaleTitle="Legacy Businesses"
        defaultCsvExportParams={defaultCsvExportParams}
        csvExportOptions={csvExportOptions}
        onRowSelectionChanged={onRowSelectionChange}
        rowSelection={RowSelectionMode.MULTIPLE}
        rowsSelectable
      />
      {renamingDataSet && (
        <RenameDataSetModal
          dataSet={renamingDataSet}
          onClose={() => setRenamingDataSet(null)}
        />
      )}
      {editingDataSet && (
        <ZoomyDialog
          open
          onClose={() => setEditingDataSet(null)}
          maxWidth="md"
          fullWidth
        >
          <MappingStep
            dataSet={editingDataSet}
            onDone={() => setEditingDataSet(null)}
          />
        </ZoomyDialog>
      )}
    </Column>
  );
}
